import { useEffect, useState } from 'react';
import { signOutSession } from '@/lib/sign-out';
import { debug } from '../utils/debug';
import versionData from '../version.json';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_FILE_PATH = '/version.json';
export const FORCE_UPDATE_APPLIED_KEY = 'superclinic:force-update-applied';

// The version baked into this running bundle at build time.
const runningVersion = versionData.version;

const getAppliedForceUpdate = (): string | null => {
  try {
    return window.sessionStorage?.getItem(FORCE_UPDATE_APPLIED_KEY) ?? null;
  } catch {
    return null;
  }
};

const markForceUpdateApplied = (minimumVersion: string): boolean => {
  try {
    window.sessionStorage?.setItem(FORCE_UPDATE_APPLIED_KEY, minimumVersion);
    return true;
  } catch {
    // Storage blocked — do not reload, or a misconfigured minimum_version
    // would put the tab in an infinite reload loop.
    return false;
  }
};

export const useVersionCheck = () => {
  const [needsRefresh, setNeedsRefresh] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`${VERSION_FILE_PATH}?t=${Date.now()}`, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.ok) return;

        const data = await response.json();

        // Remote kill switch: bundles older than minimum_version must update
        // immediately, optionally signing the user out first. Guarded per
        // minimum_version value so a misconfigured version.json (e.g.
        // minimum_version newer than the deployed bundle) cannot cause a
        // reload loop.
        if (data.minimum_version && runningVersion < data.minimum_version) {
          if (getAppliedForceUpdate() === data.minimum_version) return;
          if (!markForceUpdateApplied(data.minimum_version)) return;

          if (data.force_logout) {
            try {
              await signOutSession();
            } catch (signOutError) {
              debug.error('Forced sign-out before update failed:', signOutError);
            }
          }

          window.location.reload();
          return;
        }

        // 'initial' is the committed placeholder in public/version.json —
        // only real deploy stamps (or force-update-local) should prompt.
        if (data.version && data.version !== 'initial' && data.version !== runningVersion) {
          setNeedsRefresh(true);
        }
      } catch (error) {
        debug.error('Version check failed:', error);
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return { needsRefresh, handleRefresh };
};
